import { $sheet } from '../SheetView';
import { $selections } from '../selection';
import {
  $highlightTable,
  useHighlightedRenderedCell,
  useHighlightedTable,
} from '../utils';
import { ActionButton, ActionButtonMenu, ActionMenu } from './ActionButton';
import { ColorInput } from './ColorInput';
import { FontPicker } from './FontPicker';
import { NumberInput } from './NumberInput';
import { RibbonTooltip } from './RibbonTooltip';
import { $actionEvent } from '@/actions/actionEvent';
import { availableFonts, getAvailableFont } from '@/fonts/fonts';
import {
  type BorderStyle,
  type CellStyle,
  DEFAULT_CELL_STYLE,
  type LineStyle,
} from '@/model/cellStyle';
import { BLACK } from '@/model/excelColor';
import type { StyleToMerge } from '@/model/types';
import { $redoDesc, $undoDesc } from '@/undo';
import {
  $selectedCellStyle,
  $selectedComputedStyle,
} from '@/utils/selectUtils';
import { Icon } from '@iconify/react';
import { useStore } from '@nanostores/react';
import clsx from 'clsx';
import { atom, computed } from 'nanostores';
import { DropdownMenu, Tooltip } from 'radix-ui';
import type React from 'react';

const $borderStyle = atom<BorderStyle>({
  color: BLACK,
  width: 1,
  style: 'solid',
});
const $cellStyle = atom<StyleToMerge<CellStyle>>({});

export const ribbonBars = {
  Home: () => (
    <RibbonBar>
      <ActionButton
        name="Print"
        icon={<Icon icon="fluent:print-20-regular" />}
      />
      <div className="flex items-stretch">
        <RibbonTooltip name="Font">
          <div className="flex items-center rounded-l border border-r-0 border-gray-300 px-1">
            <FontPicker
              font={getAvailableFont(useStore($selectedComputedStyle).font)}
              fontList={availableFonts}
              onSelectFont={font => {
                $actionEvent.emit('cellStyle', {
                  font: font.family,
                });
              }}
            />
          </div>
        </RibbonTooltip>
        <RibbonTooltip name="Size">
          <NumberInput
            className="rounded-l-none"
            value={useStore($selectedComputedStyle).size}
            onChange={size => {
              $actionEvent.emit('cellStyle', {
                size,
              });
            }}
          />
        </RibbonTooltip>
      </div>
      <ActionButton
        name="Undo"
        icon={<Icon icon="fluent:arrow-undo-20-regular" />}
        disabled={!useStore($undoDesc)}
        tooltip={useStore($undoDesc) ?? 'Undo'}
      />
      <ActionButton
        name="Redo"
        icon={<Icon icon="fluent:arrow-redo-20-regular" />}
        disabled={!useStore($redoDesc)}
        tooltip={useStore($redoDesc) ?? 'Redo'}
      />
      <ActionButton
        name="Bold"
        onClick={() =>
          $actionEvent.emit('cellStyle', {
            bold: !$selectedComputedStyle.get().bold,
          })
        }
        disabled={
          !getAvailableFont(useStore($selectedComputedStyle).font).hasBold
        }
        active={!!useStore($selectedComputedStyle).bold}
        icon={<Icon icon="fluent:text-bold-20-regular" />}
      />
      <ActionButton
        name="Italic"
        onClick={() =>
          $actionEvent.emit('cellStyle', {
            italic: !$selectedComputedStyle.get().italic,
          })
        }
        disabled={
          !getAvailableFont(useStore($selectedComputedStyle).font).hasItalic
        }
        active={!!useStore($selectedComputedStyle).italic}
        icon={<Icon icon="fluent:text-italic-20-regular" />}
      />
      <ActionButton
        name="Underline"
        onClick={() =>
          $actionEvent.emit('cellStyle', {
            underline: !$selectedComputedStyle.get().underline,
          })
        }
        active={!!useStore($selectedComputedStyle).underline}
        icon={<Icon icon="fluent:text-underline-20-regular" />}
      />
      <ActionButton
        name="Strikethrough"
        onClick={() =>
          $actionEvent.emit('cellStyle', {
            strikethrough: !$selectedComputedStyle.get().strikethrough,
          })
        }
        active={!!useStore($selectedComputedStyle).strikethrough}
        icon={<Icon icon="fluent:text-strikethrough-20-regular" />}
      />
      <ActionButton
        name="Text Wrap"
        onClick={() =>
          $actionEvent.emit('cellStyle', {
            textWrap: !$selectedCellStyle.get().textWrap,
          })
        }
        active={!!useStore($selectedCellStyle).textWrap}
        icon={<Icon icon="fluent:text-wrap-20-regular" />}
      />
      <ActionButtonMenu
        name="Format as Table"
        icon={<Icon icon="fluent:table-add-20-regular" />}
        disabled={useStore(
          computed([$selections, $sheet], (selections, sheet) => {
            if (!sheet || selections.length !== 1) return true;
            const selection = selections[0]!;
            if (selection.isPointRange()) return true;
            const tables = sheet.getTablesInRange(selection);
            return !tables[Symbol.iterator]().next().done;
          }),
        )}
        dropDownDisabled={!useHighlightedTable()}
      >
        <DropdownMenu.Sub>
          <DropdownMenu.SubTrigger className="flex items-center gap-2 p-2">
            Header color
            <div
              className="h-8 w-8 rounded-sm"
              style={{
                // FIXME: gotta click out and back on the table to update the
                // color after changing
                backgroundColor:
                  useHighlightedTable()?.style?.headerColor?.toHex() ??
                  '#000000',
              }}
            />
            <Icon icon="fluent:chevron-right-20-regular" />
          </DropdownMenu.SubTrigger>
          <DropdownMenu.Portal>
            <DropdownMenu.SubContent className="ml-2 bg-white">
              <ColorInput
                color={useHighlightedTable()?.style.headerColor}
                onChange={color =>
                  $actionEvent.emit('tableStyle', { headerColor: color })
                }
              />
            </DropdownMenu.SubContent>
          </DropdownMenu.Portal>
        </DropdownMenu.Sub>
        <DropdownMenu.Sub>
          <DropdownMenu.SubTrigger className="flex items-center gap-2 p-2">
            Primary color
            <div
              className="h-8 w-8 rounded-sm"
              style={{
                backgroundColor:
                  useHighlightedTable()?.style?.primaryColor?.toHex() ??
                  '#000000',
              }}
            />
            <Icon icon="fluent:chevron-right-20-regular" />
          </DropdownMenu.SubTrigger>
          <DropdownMenu.Portal>
            <DropdownMenu.SubContent className="ml-2 bg-white">
              <ColorInput
                color={useHighlightedTable()?.style.primaryColor}
                onChange={color =>
                  $actionEvent.emit('tableStyle', { primaryColor: color })
                }
              />
            </DropdownMenu.SubContent>
          </DropdownMenu.Portal>
        </DropdownMenu.Sub>
        <DropdownMenu.Sub>
          <DropdownMenu.SubTrigger className="flex items-center gap-2 p-2">
            Secondary color
            <div
              className="h-8 w-8 rounded-sm"
              style={{
                backgroundColor:
                  useHighlightedTable()?.style?.secondaryColor?.toHex() ??
                  '#000000',
              }}
            />
            <Icon icon="fluent:chevron-right-20-regular" />
          </DropdownMenu.SubTrigger>
          <DropdownMenu.Portal>
            <DropdownMenu.SubContent className="ml-2 bg-white">
              <ColorInput
                color={useHighlightedTable()?.style.secondaryColor}
                onChange={color =>
                  $actionEvent.emit('tableStyle', { secondaryColor: color })
                }
              />
            </DropdownMenu.SubContent>
          </DropdownMenu.Portal>
        </DropdownMenu.Sub>
        <DropdownMenu.Item>
          <button
            className={clsx(
              'cursor-pointer p-1',
              useStore(useStore($sheet)?.$mainTable ?? atom(null)) ===
                useHighlightedTable()
                ? 'bg-gray-100 hover:bg-gray-200'
                : 'hover:bg-gray-100',
            )}
            onClick={() => {
              const sheet = $sheet.get();
              if (!sheet) return;
              const table = $highlightTable.get();
              if (!table) return;
              sheet.mainTable = table;
            }}
          >
            Set main
          </button>
          <button
            className={clsx(
              'cursor-pointer p-1',
              useStore(useStore($sheet)?.$headerTable ?? atom(null)) ===
                useHighlightedTable()
                ? 'bg-gray-100 hover:bg-gray-200'
                : 'hover:bg-gray-100',
            )}
            onClick={() => {
              const sheet = $sheet.get();
              if (!sheet) return;
              const table = $highlightTable.get();
              if (!table) return;
              sheet.headerTable = table;
            }}
          >
            Set header
          </button>
          <button
            className={clsx(
              'cursor-pointer p-1',
              useStore(useStore($sheet)?.$footerTable ?? atom(null)) ===
                useHighlightedTable()
                ? 'bg-gray-100 hover:bg-gray-200'
                : 'hover:bg-gray-100',
            )}
            onClick={() => {
              const sheet = $sheet.get();
              if (!sheet) return;
              const table = $highlightTable.get();
              if (!table) return;
              sheet.footerTable = table;
            }}
          >
            Set footer
          </button>
        </DropdownMenu.Item>
        <DropdownMenu.Item>
          <button
            className="cursor-pointer p-1 hover:bg-gray-100"
            onClick={() => {
              const table = $highlightTable.get();
              if (!table) return;
              $actionEvent.emit('tableStyle', {
                stretchToFit: !table.style.stretchToFit,
              });
            }}
          >
            Stretch to fit
          </button>
        </DropdownMenu.Item>
      </ActionButtonMenu>
      <ActionButtonMenu
        name="Border Outside"
        onClick={() => $actionEvent.emit('borderOutside', $borderStyle.get())}
        icon={<Icon icon="material-symbols:border-outer" />}
      >
        <DropdownMenu.Item className="grid grid-cols-5 outline-none">
          <ActionButton
            name="Border Top"
            onClick={() => {
              $actionEvent.emit('borderTop', $borderStyle.get());
            }}
            hideText
            icon={<Icon icon="material-symbols:border-top" />}
          />
          <ActionButton
            name="Border Left"
            onClick={() => {
              $actionEvent.emit('borderLeft', $borderStyle.get());
            }}
            hideText
            icon={<Icon icon="material-symbols:border-left" />}
          />
          <ActionButton
            name="Border Bottom"
            onClick={() => {
              $actionEvent.emit('borderBottom', $borderStyle.get());
            }}
            hideText
            icon={<Icon icon="material-symbols:border-bottom" />}
          />
          <ActionButton
            name="Border Right"
            onClick={() => {
              $actionEvent.emit('borderRight', $borderStyle.get());
            }}
            hideText
            icon={<Icon icon="material-symbols:border-right" />}
          />
          <ActionButton
            name="Border Horizontal"
            onClick={() => {
              $actionEvent.emit('borderHorizontal', $borderStyle.get());
            }}
            hideText
            icon={<Icon icon="material-symbols:border-horizontal" />}
          />
          <ActionButton
            name="Border Vertical"
            onClick={() => {
              $actionEvent.emit('borderVertical', $borderStyle.get());
            }}
            hideText
            icon={<Icon icon="material-symbols:border-vertical" />}
          />
          <ActionButton
            name="Border Inner"
            onClick={() => {
              $actionEvent.emit('borderInner', $borderStyle.get());
            }}
            hideText
            icon={<Icon icon="material-symbols:border-inner" />}
          />
          <ActionButton
            name="Border All"
            onClick={() => {
              $actionEvent.emit('borderAll', $borderStyle.get());
            }}
            hideText
            icon={<Icon icon="material-symbols:border-all-outline" />}
          />
          <ActionButton
            name="Border None"
            onClick={() => {
              $actionEvent.emit('borderAll', null);
            }}
            hideText
            icon={<Icon icon="material-symbols:border-clear" />}
          />
        </DropdownMenu.Item>
        <DropdownMenu.Sub>
          <DropdownMenu.SubTrigger className="flex items-center gap-2 p-2">
            Border color
            <div
              className="h-8 w-8 rounded-sm"
              style={{
                backgroundColor:
                  useStore($borderStyle).color?.toHex() ?? '#000000',
              }}
            />
            <Icon icon="fluent:chevron-right-20-regular" />
          </DropdownMenu.SubTrigger>
          <DropdownMenu.Portal>
            <DropdownMenu.SubContent className="ml-2 bg-white">
              <ColorInput
                color={useStore($borderStyle).color}
                onChange={color =>
                  $borderStyle.set({ ...$borderStyle.get(), color })
                }
              />
            </DropdownMenu.SubContent>
          </DropdownMenu.Portal>
        </DropdownMenu.Sub>
        <DropdownMenu.Sub>
          <DropdownMenu.SubTrigger className="flex items-center gap-2 p-2">
            Border width
            <input
              type="number"
              className="w-12 rounded pl-1 outline outline-black/50"
              defaultValue={useStore($borderStyle).width ?? 1}
              min="0"
              max="10"
              onChange={e =>
                $borderStyle.set({
                  ...$borderStyle.get(),
                  width: Number(e.target.value),
                })
              }
            />
          </DropdownMenu.SubTrigger>
        </DropdownMenu.Sub>
        <DropdownMenu.Sub>
          <DropdownMenu.SubTrigger className="flex items-center gap-2 p-2">
            Style
            <select
              className="w-24 rounded outline outline-black/50"
              defaultValue={useStore($borderStyle).style ?? 'Unset'}
              onChange={e =>
                $borderStyle.set({
                  ...$borderStyle.get(),
                  style: e.target.value as LineStyle,
                })
              }
            >
              <option value="solid">Solid</option>
              <option value="dashed">Dashed</option>
              <option value="dotted">Dotted</option>
              <option value="double">Double</option>
            </select>
          </DropdownMenu.SubTrigger>
        </DropdownMenu.Sub>
      </ActionButtonMenu>
      <ActionButtonMenu
        name="Background Color"
        icon={
          <Icon
            icon="fluent:paint-bucket-20-filled"
            color={useStore(
              computed($cellStyle, ({ background }) => {
                if (background === 'unset') return '#000000';
                return background?.toHex() ?? '#000000';
              }),
            )}
          />
        }
        onClick={() => {
          $actionEvent.emit('cellStyle', {
            background: $cellStyle.get().background,
          });
        }}
      >
        <ColorInput
          color={useStore($cellStyle).background}
          allowNull
          onChange={color => {
            $cellStyle.set({ ...$cellStyle.get(), background: color });
            $actionEvent.emit('cellStyle', {
              background: color,
            });
          }}
        />
      </ActionButtonMenu>
      <ActionButtonMenu
        name="Text Color"
        icon={
          <Icon
            icon="fluent:text-color-20-filled"
            color={useStore(
              computed($cellStyle, ({ color }) => {
                if (color === 'unset') return '#000000';
                return color?.toHex() ?? '#000000';
              }),
            )}
          />
        }
        onClick={() => {
          $actionEvent.emit('cellStyle', {
            color: $cellStyle.get().color,
          });
        }}
      >
        <ColorInput
          color={useStore($cellStyle).color}
          onChange={color => {
            $cellStyle.set({ ...$cellStyle.get(), color });
            $actionEvent.emit('cellStyle', {
              color,
            });
          }}
        />
      </ActionButtonMenu>
      <ActionMenu
        name="Alignment"
        icon={
          <Icon
            icon={(c => {
              let v = 'middle';
              let h = 'left';

              if (c?.style?.verticalAlign) {
                v = c.style.verticalAlign;
              }
              if (c?.style?.justify) {
                h = c.style.justify;
              }

              if (v === 'middle' && h === 'center') {
                return 'fluent:textbox-align-center-20-regular';
              }

              return `fluent:textbox-align-${v}-${h}-20-regular`;
            })(useHighlightedRenderedCell())}
          />
        }
      >
        <div className="flex flex-col justify-around gap-2 p-2 select-none">
          <div className="flex items-center gap-2">
            <DropdownMenu.Item>
              <ActionButton
                name="Text Left"
                icon={<Icon icon="fluent:text-align-left-20-regular" />}
                active={
                  (useHighlightedRenderedCell()?.style?.justify ??
                    DEFAULT_CELL_STYLE.justify) === 'left'
                }
                onClick={() => {
                  $actionEvent.emit('cellStyle', {
                    justify: 'left',
                  });
                }}
                hideText
              ></ActionButton>
            </DropdownMenu.Item>
            <DropdownMenu.Item>
              <ActionButton
                name="Text Center"
                icon={<Icon icon="fluent:text-align-center-20-regular" />}
                active={
                  (useHighlightedRenderedCell()?.style?.justify ??
                    DEFAULT_CELL_STYLE.justify) === 'center'
                }
                onClick={() => {
                  $actionEvent.emit('cellStyle', {
                    justify: 'center',
                  });
                }}
                hideText
              ></ActionButton>
            </DropdownMenu.Item>
            <DropdownMenu.Item>
              <ActionButton
                name="Text Right"
                icon={<Icon icon="fluent:text-align-right-20-regular" />}
                active={
                  (useHighlightedRenderedCell()?.style?.justify ??
                    DEFAULT_CELL_STYLE.justify) === 'right'
                }
                onClick={() => {
                  $actionEvent.emit('cellStyle', {
                    justify: 'right',
                  });
                }}
                hideText
              ></ActionButton>
            </DropdownMenu.Item>
          </div>
          <div className="flex items-center gap-2">
            <DropdownMenu.Item>
              <ActionButton
                name="Text Top"
                icon={<Icon icon="fluent:textbox-align-top-20-regular" />}
                active={
                  (useHighlightedRenderedCell()?.style?.verticalAlign ??
                    DEFAULT_CELL_STYLE.verticalAlign) === 'top'
                }
                onClick={() => {
                  $actionEvent.emit('cellStyle', {
                    verticalAlign: 'top',
                  });
                }}
                hideText
              ></ActionButton>
            </DropdownMenu.Item>
            <DropdownMenu.Item>
              <ActionButton
                name="Text Middle"
                icon={<Icon icon="fluent:textbox-align-center-20-regular" />}
                active={
                  (useHighlightedRenderedCell()?.style?.verticalAlign ??
                    DEFAULT_CELL_STYLE.verticalAlign) === 'middle'
                }
                onClick={() => {
                  $actionEvent.emit('cellStyle', {
                    verticalAlign: 'middle',
                  });
                }}
                hideText
              ></ActionButton>
            </DropdownMenu.Item>
            <DropdownMenu.Item>
              <ActionButton
                name="Text Bottom"
                icon={<Icon icon="fluent:textbox-align-bottom-20-regular" />}
                active={
                  (useHighlightedRenderedCell()?.style?.verticalAlign ??
                    DEFAULT_CELL_STYLE.verticalAlign) === 'bottom'
                }
                onClick={() => {
                  $actionEvent.emit('cellStyle', {
                    verticalAlign: 'bottom',
                  });
                }}
                hideText
              ></ActionButton>
            </DropdownMenu.Item>
          </div>
          <div className="flex items-center gap-2">
            <span className="text-sm">Line height</span>
            <NumberInput
              value={
                useStore($selectedCellStyle).lineHeight ??
                DEFAULT_CELL_STYLE.lineHeight
              }
              onChange={lineHeight => {
                $actionEvent.emit('cellStyle', {
                  lineHeight,
                });
              }}
              step={0.1}
              min={1}
              max={3}
            />
          </div>
        </div>
      </ActionMenu>
    </RibbonBar>
  ),
  Insert: () => <RibbonBar>Insert</RibbonBar>,
  'Page Layout': () => <RibbonBar>Page Layout</RibbonBar>,
  Formula: () => <RibbonBar>Formula</RibbonBar>,
  Data: () => <RibbonBar>Data</RibbonBar>,
  View: () => <RibbonBar>View</RibbonBar>,
  Help: () => <RibbonBar>Help</RibbonBar>,
};

export const $selectedRibbonBar = atom<keyof typeof ribbonBars>('Home');

function RibbonBar({ children }: React.PropsWithChildren) {
  return (
    <Tooltip.Provider>
      <div
        className="m-2 flex items-center justify-start gap-2 rounded-md bg-white p-1 shadow-md"
        onMouseDown={e => e.stopPropagation()}
        onKeyDown={e => e.stopPropagation()}
        onClick={e => e.stopPropagation()}
      >
        {children}
      </div>
    </Tooltip.Provider>
  );
}
